
import Vue from "vue";
import Component from "vue-class-component";
import FileUploader from "@/components/FileUploader.vue";
import DropdownSelector from "@/components/DropdownSelector.vue";
import Files from "@/components/Files.vue";
import FormLayout from "@/features/personProfile/components/FormLayout.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";

import {
    AddSeafaringPracticeRequest, AreaOption,
    AreasClient,
    CargoOperationModel,
    CountryLightModel,
    DocumentAttributesClient,
    HarbourOption,
    HarboursClient,
    HarbourVisitModel,
    OrganizationOption,
    PersonOption,
    PositionOption,
    PositionsClient,
    PracticeKind,
    PracticePeriod,
    PracticeType,
    PracticesClient, ResultError,
    SeafaringPracticeActions,
    ShipTypeLocalizedModel,
    ShipTypesClient,
    FindOverlappingPracticesRequest,
    IFindOverlappingPracticesRequest
} from "@/api-client";
import PracticePeriods from "@/features/documents/practices/components/PracticePeriods.vue";
import ShipInformation from "@/features/documents/practices/components/ShipInformation.vue";
import CargoOperationsTable from "@/features/documents/practices/components/CargoOperationsTable.vue";
import CargoOperationModal from "@/features/documents/practices/components/CargoOperationModal.vue";
import HarbourVisitsTable from "@/features/documents/practices/components/HarbourVisitsTable.vue";
import HarbourVisitModal from "@/features/documents/practices/components/HarbourVisitModal.vue";
import CardQuicklink from "@/features/dashboard/components/CardQuicklink.vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import {Prop} from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import userInfo from "@/features/shared/components/UserInfo";
import moment from "moment";

interface Props {
    seafarerId: string;
}

@Component({
    components: {
        ButtonWithConfirm,
        CardQuicklink,
        CargoOperationModal,
        CargoOperationsTable,
        CheckboxInput,
        DropdownSelector,
        Files,
        FileUploader,
        FormLayout,
        HarbourVisitModal,
        HarbourVisitsTable,
        HeadingGroup,
        LabelOptional,
        PracticePeriods,
        ShipInformation,
    }
})
export default class SeafaringPracticeUpsertForm extends Vue implements Props {
    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    model!: AddSeafaringPracticeRequest;

    @Prop({required: true})
    actions!: SeafaringPracticeActions;

    @Prop({required: true})
    submitCallback!: (model: AddSeafaringPracticeRequest) => Promise<void>;

    validationErrors = ResultError.createEmpty();
    positionOptions: PositionOption[] = [];
    organizationOptions: OrganizationOption[] = [];
    areaOptions: AreaOption[] = [];
    shipTypeOptions: ShipTypeLocalizedModel[] = [];
    harbourOptions: HarbourOption[] = [];
    confirmerOptions: PersonOption[] = [];
    countries: CountryLightModel[] = [];

    warningMatching = false;

    isOrganizationEnteredManually: boolean =
        !this.model.organizationId && !!this.model.organizationName;
    isConfirmerNameInsertedManually: boolean =
        !this.model.confirmerId && !!this.model.confirmer;

    practiceKindOptions = [
        {text: this.$ls.Documents_Practices_PracticeKind_Educational, value: PracticeKind.Educational},
        {text: this.$ls.Documents_Practices_PracticeKind_Work, value: PracticeKind.Work},
    ];

    isCreated = false;
    fileLoader = new Loader();
    submitLoader = new Loader();
    isSeafarer = userInfo.seafarerId === this.seafarerId;

    async created() {
        await Promise.all(
            [
                new DocumentAttributesClient().countries().then(x => this.countries = x),
                new PositionsClient().activePositionOptions().then(x => this.positionOptions = x),
                new PracticesClient().seafaringOrganizationOptions().then(x => this.organizationOptions = x.organizationOptions.filter(o => o.active)),
                new AreasClient().areaOptions().then(x => this.areaOptions = x.areas),
                new ShipTypesClient().shipTypesLocalized("").then(x => this.shipTypeOptions = x.shipTypesLocalized.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))),
                new HarboursClient().harbourOptions().then(x => this.harbourOptions = x.harbourOptions)
            ]
        );

        if (!this.model.kind) {
            if (this.actions.canStartEnteringEducational && !this.actions.canStartEnteringWorking) {
                this.model.kind = PracticeKind.Educational;
            }
            if (this.actions.canStartEnteringWorking && !this.actions.canStartEnteringEducational) {
                this.model.kind = PracticeKind.Work;
            }
        }

        if (!this.model.withPracticeCertificate && // Do not override existing 'true' value.
            !this.actions.canChooseWithOrWithoutFile)
        {
            this.model.withPracticeCertificate = false;
        }

        if (this.organizationOptions.length > 0 && !this.actions.canChooseOrganizationAndConfirmer) {
            this.confirmerOptions = this.organizationOptions[0].personOptions;
            this.model.confirmerId = this.confirmerOptions[0].value;
            this.model.organizationId = this.organizationOptions[0].value;
            this.model.organizationName = this.organizationOptions[0].text;
        }

        if (!this.model.practicePeriods) {
            this.model.practicePeriods = [];
        }

        this.$watch(() => this.model, () => {
            this.validationErrors.clearKey("practicePeriods");
            if(this.model.issuedOn && moment().startOf("day").diff(this.model.issuedOn) < 0){
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issuedOn");
            }
            else{
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.clear();
                if (this.model.issuedOn && this.model.practicePeriods.length > 0) {
                    this.model.practicePeriods.forEach(period => {
                        if (moment(period.startedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                        if (moment(period.endedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                    });
                }
            }
        },{deep:true});

        this.$watch(
            () => this.isConfirmerNameInsertedManually,
            () => this.resetConfirmer()
        );

        this.$watch(
            () => this.isOrganizationEnteredManually,
            () => this.organizationEnteredManuallyChanged()
        );

        this.$watch(
            () => this.model.organizationId,
            () => {
                if (this.model.organizationId)
                    this.setConfirmerOptions();
            },
            { immediate: true }
        );

        this.isCreated = true;
    }

    async submit() {
        if(!this.validationErrors.isEmpty){
            return;
        }
      this.submitLoader.startLoading();
        this.validationErrors.clear();
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch((error: ResultError) => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure))
      this.submitLoader.endLoading();
    }

    private addPracticePeriod(practicePeriod: PracticePeriod) {
         if (this.model.positionId) {
            const requestModel: IFindOverlappingPracticesRequest = {
                positionId: this.model.positionId,
                practiceType: PracticeType.Seafaring,
                practicePeriod: practicePeriod
            }
            Promise.all([
                new PracticesClient().hasOverlap(this.seafarerId, new FindOverlappingPracticesRequest(requestModel))
            ]).then(value => {
                this.warningMatching = value[0];
            });
        }
        this.model.practicePeriods.push(practicePeriod);
    }

    private removePracticePeriod(practicePeriod: PracticePeriod) {
        this.$delete(
            this.model.practicePeriods,
            this.model.practicePeriods.indexOf(practicePeriod)
        );
    }

    private editPracticePeriod(practicePeriod: PracticePeriod, index:number) {

        this.model.practicePeriods[index] = practicePeriod; 
    }

    private translatePracticeKind(kind: PracticeKind | undefined | null): string {
        if (!kind)
            return "";

        return this.practiceKindOptions.find((o) => o.value == kind)!.text
    }

    private setWithCargoOperations() {
        this.model.withCargoOperations = true;
    }

    private setWithoutCargoOperations() {
        this.model.cargoOperations = [];
        this.model.withCargoOperations = false;
    }

    private addCargoOperation(cargoOperation: CargoOperationModel) {
        this.model.cargoOperations.push(cargoOperation);
    }

    private removeCargoOperation(cargoOperation: CargoOperationModel) {
        this.$delete(
            this.model.cargoOperations,
            this.model.cargoOperations.indexOf(cargoOperation)
        );
    }

    private setWithHarbourVisits() {
        this.model.withHarbourVisits = true;
    }

    private setWithoutHarbourVisits() {
        this.model.harbourVisits = [];
        this.model.withHarbourVisits = false;
    }

    private addHarbourVisit(harbourVisit: HarbourVisitModel) {
        this.model.harbourVisits.push(harbourVisit);
    }

    private removeHarbourVisit(harbourVisit: HarbourVisitModel) {
        this.$delete(
            this.model.harbourVisits,
            this.model.harbourVisits.indexOf(harbourVisit)
        );
    }

    private organizationEnteredManuallyChanged() {
        if (this.isOrganizationEnteredManually) {
            this.model.confirmerId = null;
            this.isConfirmerNameInsertedManually = true;
            this.confirmerOptions = [];
        } else {
            this.isConfirmerNameInsertedManually = false;
        }

        this.resetOrganization();
    }

    private resetConfirmer() {
        this.model.confirmerId = null;
        this.model.confirmer = null;
    }

    private resetOrganization() {
        this.model.organizationId = null;
        this.model.organizationName = "";
    }

    private setConfirmerOptions() {
        const organization = this.organizationOptions.find(o => o.value == this.model.organizationId);
        this.confirmerOptions = organization ? organization.personOptions : []

        this.isConfirmerNameInsertedManually = this.hasNoConfirmerOptions;
    }

    private get hasNoConfirmerOptions() {
        return this.confirmerOptions.length === 0;
    }

    private get hasAnyConfirmerOptions() {
        return !this.hasNoConfirmerOptions;
    }
}
